type FetchPlatformInternal = (path: string, init: RequestInit, label: string) => Promise<Response>;
type LoggerLike = Pick<Console, "warn">;

export interface PlatformSpendCapCache {
  allowed: boolean;
  remaining?: number;
  expiresAtMs: number;
}

export function createPlatformSpendCapCache(): PlatformSpendCapCache {
  return { allowed: true, expiresAtMs: 0 };
}

export function shouldMeterPlatformRequestPath(path: string): boolean {
  if (
    path.startsWith("/_admin") ||
    path.startsWith("/_dashboard") ||
    path.startsWith("/_proxy/") ||
    path === "/ws" ||
    path === "/health" ||
    path.startsWith("/health/") ||
    path.startsWith("/internal/") ||
    path === "/api/auth" ||
    path.startsWith("/api/auth/")
  ) {
    return false;
  }
  return true;
}

export async function checkPlatformSpendCapForRequest(params: {
  internalToken: string;
  appName?: string;
  appDbId?: string | number;
  cache: PlatformSpendCapCache;
  fetchPlatformInternal: FetchPlatformInternal;
  logger?: LoggerLike;
  nowMs?: number;
}): Promise<{ allowed: boolean; remaining?: number }> {
  const nowMs = params.nowMs ?? Date.now();
  if (params.cache.expiresAtMs > nowMs) {
    return { allowed: params.cache.allowed, remaining: params.cache.remaining };
  }

  if (!params.internalToken) {
    return { allowed: true };
  }

  const logger = params.logger ?? console;
  try {
    const response = await params.fetchPlatformInternal(
      "/internal/spend-cap",
      {
        method: "GET",
        headers: {
          "X-Internal-Token": params.internalToken,
          ...(params.appDbId ? { "X-Gencow-App-Id": String(params.appDbId) } : {}),
          ...(params.appName ? { "X-Gencow-App-Name": params.appName } : {}),
        },
      },
      "spend-cap-check",
    );
    if (!response.ok) {
      logger.warn(`[metering] spend-cap check failed: ${response.status}`);
      return { allowed: true };
    }

    const body = (await response.json()) as { allowed?: boolean; remaining?: number };
    const allowed = body.allowed !== false;
    params.cache.allowed = allowed;
    params.cache.remaining = Number.isFinite(body.remaining) ? Number(body.remaining) : undefined;
    params.cache.expiresAtMs = nowMs + (allowed ? 5_000 : 30_000);
    return { allowed, remaining: params.cache.remaining };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`[metering] spend-cap check error: ${message}`);
    return { allowed: true };
  }
}
